/* import __COLOCATED_TEMPLATE__ from './project-details-activity-action-item.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { ACTION } from 'sh-www/models/action';
import { makeMoney } from 'sh-www/helpers/make-money';
import { PROJECT_DETAILS__ACTIONS_WITH_PRIORITY } from 'sh-www/constants/routes/project-details';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { USER } from 'sh-www/models/user';
import { APPLICATION_SOURCE } from 'sh-www/models/application';
import clsx from 'clsx';

const BOLDED_ACTIONS = PROJECT_DETAILS__ACTIONS_WITH_PRIORITY;
const CLASS_NAME = 'SH-c-project-details-activity-action-item';
const BOLD_ACTION_CLASS_NAME = `${CLASS_NAME}--bold-action`;

class ProjectDetailsActivityActionItem extends Component {
  @service session;

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.isBoldAction && BOLD_ACTION_CLASS_NAME,
    );
  }

  get actionText() {
    const { action } = this.args;
    const { oldValue, newValue, details } = action;
    const actionUserCurrentUserNameOrYou = action.user?.id === this.session.currentUser.id
      ? 'You'
      : action.user?.firstName;

    if (this.session.currentUser.isBuyer) {
      switch (action.name) {
        case ACTION.NAMES.ACTION_PROJECT_ACCEPT:
          if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_DIRECT]) {
            return `${actionUserCurrentUserNameOrYou} accepted the project`;
          }

          return `${actionUserCurrentUserNameOrYou} accepted the offer`;
        case ACTION.NAMES.PROJECT_ACTIVE:
          return `Project started`;
        case ACTION.NAMES.PROJECT_CANCELLED:
          return `Project cancelled`;
        case ACTION.NAMES.PROJECT_AUTO_CANCELED:
          return 'Project auto-canceled due to inactivity';
        case ACTION.NAMES.PROJECT_AUTO_DECLINED:
          return 'Application auto-declined due to inactivity';
        case ACTION.NAMES.PROJECT_CANCELLED_STALE:
          return `${actionUserCurrentUserNameOrYou} canceled this project due to inactivity`;
        case ACTION.NAMES.PROJECT_COMPLETED:
          return `Project completed`;
        case ACTION.NAMES.PROJECT_EXPENSE_CREATE:
          if (details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]) {
            return `${actionUserCurrentUserNameOrYou} added expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]}' for ${makeMoney([newValue])}`;
          }

          return `${actionUserCurrentUserNameOrYou} added an expense for ${makeMoney([newValue])}`;
        case ACTION.NAMES.PROJECT_EXPENSE_DELETE:
          if (details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]) {
            return `${actionUserCurrentUserNameOrYou} removed expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]}'`;
          }

          return `${actionUserCurrentUserNameOrYou} removed an expense`;
        case ACTION.NAMES.PROJECT_EXPENSE_UPDATE:
          return `${actionUserCurrentUserNameOrYou} changed expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]}' for ${makeMoney([oldValue])} to '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]}' for ${makeMoney([newValue])}`;
        case ACTION.NAMES.PROJECT_EXPENSES_ENABLED:
          return `${actionUserCurrentUserNameOrYou} enabled expenses`;
        case ACTION.NAMES.PROJECT_EXPENSES_DISABLED:
          return `${actionUserCurrentUserNameOrYou} disabled expenses`;
        case ACTION.NAMES.PROJECT_BUYER_COVER_SELLER_FEES:
          return `${actionUserCurrentUserNameOrYou} agreed to cover creator fees`;
        case ACTION.NAMES.PROJECT_APPROVE_PRICE:
          return `${actionUserCurrentUserNameOrYou} accepted the offer`;
        case ACTION.NAMES.PROJECT_ADVANCE_AGREED: {
            if (action.user.isBuyer) {
              // Buyer viewing advance accepted by buyer
              return `${actionUserCurrentUserNameOrYou} accepted the advance request`;
            }
            // Buyer viewing advance accepted by seller
            return `${action.user.firstName} accepted your advance`;
        }
        case ACTION.NAMES.PROJECT_ADVANCE_CANCEL: {
          // If same user, this is a cancellation
          if (action.details.advanceRequestorId === action.user.id) {
            return `${actionUserCurrentUserNameOrYou} cancelled the advance`;
          }

          if (action.user.isBuyer) {
            // Buyer viewing advance decline by buyer
            return `${actionUserCurrentUserNameOrYou} declined an advance request`;
          }
          // Buyer viewing advance decline by seller
          return `${action.user.firstName} declined your advance`;
        }
        case ACTION.NAMES.PROJECT_ADVANCE_CREATE: {
          if (action.user.isBuyer) {
            // Buyer viewing advance initiated by buyer
            return `${actionUserCurrentUserNameOrYou} initiated an advance for ${makeMoney([newValue])}`;
          }
          return `${actionUserCurrentUserNameOrYou} requested an advance for ${makeMoney([newValue])}`;
        }
        case ACTION.NAMES.PROJECT_CREATE:
          if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_DIRECT]) {
            if (details?.[ACTION.META_CONSTANTS.APPLICATION_SOURCE] === APPLICATION_SOURCE.QUOTE) {
              return `${actionUserCurrentUserNameOrYou} requested a quote`;
            } else {
              return `${actionUserCurrentUserNameOrYou} created a new project`;
            }
          }

          if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
            return `${actionUserCurrentUserNameOrYou} sent a pitch`;
          }

          return `${actionUserCurrentUserNameOrYou} sent an application`;
        case ACTION.NAMES.PROJECT_CANCEL_COMMIT:
          return action.user?.isAdmin
            ? 'This project was cancelled by an administrator'
            : `${actionUserCurrentUserNameOrYou} confirmed the project cancellation`;
        case ACTION.NAMES.PROJECT_CANCEL_REQUEST:
          if (newValue !== oldValue) {
            return `${actionUserCurrentUserNameOrYou} requested cancellation of this project, offering ${makeMoney([newValue])}`;
          }

          return `${actionUserCurrentUserNameOrYou} requested cancellation of this project`;
        case ACTION.NAMES.PROJECT_CANCEL_WITHDRAW:
          return `${actionUserCurrentUserNameOrYou} withdrew your cancellation request`;
        case ACTION.NAMES.PROJECT_COMPLETE_REQUEST:
        case ACTION.NAMES.PROJECT_BUYER_COMPLETE:
          return `${actionUserCurrentUserNameOrYou} marked the project complete`;
        case ACTION.NAMES.PROJECT_COMPLETE_CONFIRM:
          return `${actionUserCurrentUserNameOrYou} confirmed the project completion`;
        case ACTION.NAMES.PROJECT_DECLINE:
          if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
            return `${actionUserCurrentUserNameOrYou} declined this pitch`;
          }

          return `${actionUserCurrentUserNameOrYou} declined this application`;
        case ACTION.NAMES.PROJECT_OFFER:
          return `${actionUserCurrentUserNameOrYou} offered ${makeMoney([newValue])}`;
        case ACTION.NAMES.PROJECT_PRICE_CHANGED:
          return `${actionUserCurrentUserNameOrYou} changed the project price to ${makeMoney([newValue])}`;
        case ACTION.NAMES.PROJECT_REOPENED:
        case ACTION.NAMES.PROJECT_COMPLETE_WITHDRAW:
          return `${actionUserCurrentUserNameOrYou} marked the project active`;
        case ACTION.NAMES.PROJECT_WITHDRAW:
          if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
            return `${actionUserCurrentUserNameOrYou} withdrew their pitch`;
          }

          return `${actionUserCurrentUserNameOrYou} withdrew their application`;
      }

      return 'Unknown';
    }

    // seller viewing (or maybe an admin)
    switch (action.name) {
      case ACTION.NAMES.PROJECT_ACCEPT:
        if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_DIRECT]) {
          return `${actionUserCurrentUserNameOrYou} accepted the project`;
        }

        return `${actionUserCurrentUserNameOrYou} accepted the offer`;
      case ACTION.NAMES.PROJECT_ACTIVE:
        return `Project started`;
      case ACTION.NAMES.PROJECT_ADVANCE_AGREED: {
        switch (this.session.currentUser.type) {
          case USER.TYPES.SELLER: {
            if (action.user.isBuyer) {
              // Seller viewing advance decline by buyer
              return `${actionUserCurrentUserNameOrYou} accepted your advance request`;
            }
            return `You accepted the advance`;
          }
          case USER.TYPES.ADMIN:
            return `${action.user.fullName} accepted an advance`;
        }

        break; // Shouldn't be possible
      }
      case ACTION.NAMES.PROJECT_ADVANCE_CANCEL: {
        // If same user, this is a cancellation
        if (action.details.advanceRequestorId === action.user.id) {
          return `${actionUserCurrentUserNameOrYou} cancelled the advance`;
        }

        // Different user, so this is a decline
        switch (this.session.currentUser.type) {
          case USER.TYPES.SELLER: {
            if (action.user.isBuyer) {
              // Seller viewing advance decline by buyer
              return `${actionUserCurrentUserNameOrYou} declined your advance request`;
            }
            return `You declined the advance`;
          }
          case USER.TYPES.ADMIN:
            return `${action.user.fullName} declined an advance`;
        }

        break; // Shouldn't be possible
      }
      case ACTION.NAMES.PROJECT_ADVANCE_CREATE:
        switch (this.session.currentUser.type) {
          case USER.TYPES.SELLER: {
            if (action.user.isBuyer) {
              // Seller viewing advance initiated by buyer
              return `${actionUserCurrentUserNameOrYou} initiated an advance for ${makeMoney([newValue])}`;
            }
            return `${actionUserCurrentUserNameOrYou} requested an advance for ${makeMoney([newValue])}`;
          }
          case USER.TYPES.ADMIN:
            return `${action.user.fullName} initiated an advance for ${makeMoney([newValue])}`;
        }

        break; // Shouldn't be possible
      case ACTION.NAMES.PROJECT_APPROVE_PRICE:
        return `${actionUserCurrentUserNameOrYou} accepted the offer`;
      case ACTION.NAMES.PROJECT_CANCELLED:
        return `Project cancelled`;
      case ACTION.NAMES.PROJECT_AUTO_CANCELED:
        return 'Project auto-canceled due to inactivity';
      case ACTION.NAMES.PROJECT_AUTO_DECLINED:
        return 'Application auto-declined due to inactivity';
      case ACTION.NAMES.PROJECT_CANCELLED_STALE:
        return `${actionUserCurrentUserNameOrYou} canceled this project due to inactivity`;
      case ACTION.NAMES.PROJECT_CHANGE_PRICE:
        return `${actionUserCurrentUserNameOrYou} changed the project price to ${makeMoney([newValue])}`;
      case ACTION.NAMES.PROJECT_COMPLETED:
        return `Project completed`;
      case ACTION.NAMES.PROJECT_CREATE:
        if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_DIRECT]) {
          if (details?.[ACTION.META_CONSTANTS.APPLICATION_SOURCE] === APPLICATION_SOURCE.QUOTE) {
            return `${actionUserCurrentUserNameOrYou} requested a quote`;
          } else {
            return `${actionUserCurrentUserNameOrYou} created a new project`;
          }
        }

        if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
          return `${actionUserCurrentUserNameOrYou} sent a pitch`;
        }

        return `${actionUserCurrentUserNameOrYou} sent an application`;
      case ACTION.NAMES.PROJECT_CANCEL_COMMIT:
        return action.user?.isAdmin
          ? 'This project was cancelled by an administrator'
          : `${actionUserCurrentUserNameOrYou} confirmed the project cancellation`;
      case ACTION.NAMES.PROJECT_CANCEL_REQUEST:
        if (newValue !== oldValue) {
          return `${actionUserCurrentUserNameOrYou} requested cancellation of this project, offering ${makeMoney([newValue])}`;
        }

        return `${actionUserCurrentUserNameOrYou} requested cancellation of this project`;
      case ACTION.NAMES.PROJECT_CANCEL_WITHDRAW:
        return `${actionUserCurrentUserNameOrYou} withdrew the cancellation request`;
      case ACTION.NAMES.PROJECT_COMPLETE_REQUEST:
      case ACTION.NAMES.PROJECT_BUYER_COMPLETE:
        return `${actionUserCurrentUserNameOrYou} marked the project complete`;
      case ACTION.NAMES.PROJECT_COMPLETE_CONFIRM:
        return `${actionUserCurrentUserNameOrYou} confirmed the project completion`;
      case ACTION.NAMES.PROJECT_DECLINE:
        if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
          return `${actionUserCurrentUserNameOrYou} declined this pitch`;
        }

        return `${actionUserCurrentUserNameOrYou} declined this application`;
      case ACTION.NAMES.PROJECT_EXPENSE_CREATE:
        if (details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]) {
          return `${actionUserCurrentUserNameOrYou} added expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]}' for ${makeMoney([newValue])}`;
        }

        return `${actionUserCurrentUserNameOrYou} added an expense for ${makeMoney([newValue])}`;
      case ACTION.NAMES.PROJECT_EXPENSE_DELETE:
        if (details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]) {
          return `${actionUserCurrentUserNameOrYou} removed expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]}'`;
        }

        return `${actionUserCurrentUserNameOrYou} removed an expense`;
      case ACTION.NAMES.PROJECT_EXPENSE_UPDATE:
        return `${actionUserCurrentUserNameOrYou} changed expense '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_OLD]}' for ${makeMoney([oldValue])} to '${details?.[ACTION.META_CONSTANTS.EXPENSE_DESCRIPTION_NEW]}' for ${makeMoney([newValue])}`;
      case ACTION.NAMES.PROJECT_EXPENSES_ENABLED:
        return `${actionUserCurrentUserNameOrYou} enabled expenses`;
      case ACTION.NAMES.PROJECT_EXPENSES_DISABLED:
        return `${actionUserCurrentUserNameOrYou} disabled expenses`;
      case ACTION.NAMES.PROJECT_BUYER_COVER_SELLER_FEES:
        return `${actionUserCurrentUserNameOrYou} agreed to cover your fees`;
      case ACTION.NAMES.PROJECT_OFFER:
        return `${actionUserCurrentUserNameOrYou} offered ${makeMoney([newValue])}`;
      case ACTION.NAMES.PROJECT_PRICE_CHANGED:
        return `${actionUserCurrentUserNameOrYou} changed the project price to ${makeMoney([newValue])}`;
      case ACTION.NAMES.PROJECT_REOPENED:
      case ACTION.NAMES.PROJECT_COMPLETE_WITHDRAW:
        return `${actionUserCurrentUserNameOrYou} marked the project active`;
      case ACTION.NAMES.PROJECT_WITHDRAW:
        if (details?.[ACTION.META_CONSTANTS.ASSIGNMENT_TYPE] === ASSIGNMENT.TYPES.PITCH) {
          return `You withdrew your pitch`;
        }

        return `You withdrew your application`;
    }

    return 'Unknown';
  }

  get isBoldAction() {
    return BOLDED_ACTIONS.includes(this.args.action.name);
  }

  get isHideAction() {
    return this.actionText === 'Unknown';
  }
}

export default ProjectDetailsActivityActionItem;
